import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, TemplateRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgApexchartsModule } from 'ng-apexcharts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexDataLabels,
  ApexPlotOptions,
  ApexLegend,
  ApexTooltip,
  ApexFill,
  ApexStroke,
} from 'ng-apexcharts';
import {
  OnboardingRequestService,
  OnboardingRequest,
  OnboardingRequestStatus,
  OnboardingRequestStats,
} from '../../core/services/onboarding-request.service';
import { DataTableColumn, DataTableCellContext, PageChangeEvent } from '../../components/ui/data-table/data-table.types';
import { ComponentsModule } from '../../components/components.module';
import { BadgeColor } from '../../components/ui/badge/badge.component';

/** Referral source values from the registration form */
const REFERRAL_SOURCES = ['partner_hotel', 'event', 'online', 'sales_contact', 'other'] as const;
type ReferralSource = typeof REFERRAL_SOURCES[number];

/** Chart colors matching the Dashonic stacked bar style */
const REFERRAL_COLORS: Record<ReferralSource, string> = {
  partner_hotel: '#556ee6',
  event: '#34c38f',
  online: '#f1b44c',
  sales_contact: '#f46a6a',
  other: '#564ab1',
};

export interface ReferralChartOptions {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  legend: ApexLegend;
  tooltip: ApexTooltip;
  fill: ApexFill;
  stroke: ApexStroke;
  colors: string[];
}

@Component({
  selector: 'app-onboarding-requests',
  templateUrl: './onboarding-requests.component.html',
  styleUrls: ['./onboarding-requests.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TranslateModule, ComponentsModule, NgApexchartsModule],
})
export class OnboardingRequestsComponent implements OnInit, AfterViewInit {
  requests: OnboardingRequest[] = [];
  stats: OnboardingRequestStats = { pending: 0, contacted: 0, in_progress: 0, total: 0 };
  isLoading = false;

  // Pagination
  currentPage = 1;
  totalItems = 0;
  pageSize = 15;

  // Filter
  statusFilter: OnboardingRequestStatus | 'all' = 'all';

  // Table columns
  tableColumns: DataTableColumn<OnboardingRequest>[] = [];

  // Cell templates
  @ViewChild('hotelCell', { static: true }) hotelCellTemplate!: TemplateRef<DataTableCellContext<OnboardingRequest>>;
  @ViewChild('contactCell', { static: true }) contactCellTemplate!: TemplateRef<DataTableCellContext<OnboardingRequest>>;
  @ViewChild('dateCell', { static: true }) dateCellTemplate!: TemplateRef<DataTableCellContext<OnboardingRequest>>;
  @ViewChild('statusCell', { static: true }) statusCellTemplate!: TemplateRef<DataTableCellContext<OnboardingRequest>>;
  @ViewChild('actionsCell', { static: true }) actionsCellTemplate!: TemplateRef<DataTableCellContext<OnboardingRequest>>;

  // Referral chart
  referralChartOptions: ReferralChartOptions | null = null;
  referralChartLoading = true;

  readonly statusOptions: { value: OnboardingRequestStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'ONBOARDING_REQUESTS.ALL_STATUSES' },
    { value: 'pending', label: 'ONBOARDING_REQUESTS.STATUS.PENDING' },
    { value: 'contacted', label: 'ONBOARDING_REQUESTS.STATUS.CONTACTED' },
    { value: 'in_progress', label: 'ONBOARDING_REQUESTS.STATUS.IN_PROGRESS' },
    { value: 'completed', label: 'ONBOARDING_REQUESTS.STATUS.COMPLETED' },
    { value: 'rejected', label: 'ONBOARDING_REQUESTS.STATUS.REJECTED' },
  ];

  constructor(
    private onboardingRequestService: OnboardingRequestService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadRequests();
    this.loadReferralChart();
  }

  ngAfterViewInit(): void {
    this.setupTableColumns();
    this.cdr.detectChanges();
  }

  private setupTableColumns(): void {
    this.tableColumns = [
      {
        key: 'hotel_name',
        label: 'ONBOARDING_REQUESTS.HOTEL',
        cellTemplate: this.hotelCellTemplate,
      },
      {
        key: 'contact_name',
        label: 'ONBOARDING_REQUESTS.CONTACT',
        cellTemplate: this.contactCellTemplate,
      },
      {
        key: 'created_at',
        label: 'ONBOARDING_REQUESTS.SUBMITTED',
        cellTemplate: this.dateCellTemplate,
        sortable: true,
      },
      {
        key: 'status',
        label: 'ONBOARDING_REQUESTS.STATUS_LABEL',
        cellTemplate: this.statusCellTemplate,
      },
      {
        key: 'actions',
        label: 'COMMON.ACTION',
        cellTemplate: this.actionsCellTemplate,
        align: 'left',
      },
    ];
  }

  loadRequests(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.onboardingRequestService
      .getRequests({
        status: this.statusFilter === 'all' ? undefined : this.statusFilter,
        page: this.currentPage,
        per_page: this.pageSize,
      })
      .subscribe({
        next: (response) => {
          this.requests = response.requests.data;
          this.stats = response.stats;
          this.totalItems = response.requests.total;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadReferralChart(): void {
    this.referralChartLoading = true;

    this.onboardingRequestService.getAllRequests().subscribe({
      next: (allRequests) => {
        this.buildReferralChart(allRequests);
        this.referralChartLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.referralChartLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private buildReferralChart(allRequests: OnboardingRequest[]): void {
    // Group requests by quarter and referral source
    const quarterMap = new Map<string, Map<ReferralSource | 'unknown', number>>();

    for (const request of allRequests) {
      const date = new Date(request.created_at);
      const quarter = this.getQuarterLabel(date);
      const source = (REFERRAL_SOURCES.includes(request.how_did_you_hear as ReferralSource)
        ? request.how_did_you_hear
        : 'unknown') as ReferralSource | 'unknown';

      if (!quarterMap.has(quarter)) {
        quarterMap.set(quarter, new Map());
      }
      const sourceCounts = quarterMap.get(quarter)!;
      sourceCounts.set(source, (sourceCounts.get(source) || 0) + 1);
    }

    // Sort quarters chronologically
    const sortedQuarters = Array.from(quarterMap.keys()).sort();

    if (sortedQuarters.length === 0) {
      this.referralChartOptions = null;
      return;
    }

    // Build series — one per referral source (+ unknown)
    const allSources: (ReferralSource | 'unknown')[] = [...REFERRAL_SOURCES, 'unknown'];
    const seriesData: ApexAxisChartSeries = allSources.map((source) => ({
      name: this.getReferralSourceLabel(source),
      data: sortedQuarters.map((quarter) => quarterMap.get(quarter)?.get(source) || 0),
    }));

    // Filter out series that have no data at all
    const activeSeries = seriesData.filter((s) => (s.data as number[]).some((v) => v > 0));
    const activeColors = allSources
      .filter((_, i) => (seriesData[i].data as number[]).some((v) => v > 0))
      .map((source) => source === 'unknown' ? '#adb5bd' : REFERRAL_COLORS[source]);

    this.referralChartOptions = {
      series: activeSeries,
      chart: {
        type: 'bar',
        height: Math.max(200, 80 + sortedQuarters.length * 70),
        stacked: true,
        stackType: '100%',
        toolbar: { show: false },
        fontFamily: 'inherit',
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: sortedQuarters.length === 1 ? '35%' : '55%',
          borderRadius: 2,
        },
      },
      xaxis: {
        categories: sortedQuarters,
        labels: {
          style: { colors: '#74788d', fontSize: '12px' },
          formatter: (val: string) => `${Math.round(Number(val))}%`,
        },
      },
      yaxis: {
        labels: {
          style: { colors: '#74788d', fontSize: '13px', fontWeight: 600 },
        },
        tickAmount: 1,
      },
      dataLabels: {
        enabled: true,
        style: { fontSize: '11px', fontWeight: 600, colors: ['#fff'] },
        formatter: (val: number) => (val >= 5 ? `${Math.round(val)}%` : ''),
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '13px',
        markers: { size: 10 },
        itemMargin: { horizontal: 12 },
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} request${val !== 1 ? 's' : ''}`,
        },
      },
      fill: { opacity: 1 },
      stroke: { width: 1, colors: ['#fff'] },
      colors: activeColors,
    };
  }

  private getQuarterLabel(date: Date): string {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    return `Q${quarter} ${date.getFullYear()}`;
  }

  private getReferralSourceLabel(source: ReferralSource | 'unknown'): string {
    const key = `ONBOARDING_REQUEST.REFERRAL_OPTIONS.${source.toUpperCase()}`;
    const translated = this.translate.instant(key);
    // If translation key not found, return a readable fallback
    if (translated === key) {
      const fallbacks: Record<string, string> = {
        partner_hotel: 'Partner Hotel',
        event: 'Event',
        online: 'Online',
        sales_contact: 'Sales Contact',
        other: 'Other',
        unknown: 'Not specified',
      };
      return fallbacks[source] || source;
    }
    return translated;
  }

  onStatusFilterChange(status: OnboardingRequestStatus | 'all'): void {
    this.statusFilter = status;
    this.currentPage = 1;
    this.loadRequests();
  }

  onPageChange(event: PageChangeEvent): void {
    this.currentPage = event.page;
    this.pageSize = event.perPage;
    this.loadRequests();
  }

  navigateToDetail(request: OnboardingRequest): void {
    this.router.navigate([request.id], { relativeTo: this.route });
  }

  getStatusVariant(status: OnboardingRequestStatus): BadgeColor {
    const statusMap: Record<OnboardingRequestStatus, BadgeColor> = {
      pending: 'warning',
      contacted: 'secondary',
      in_progress: 'secondary',
      completed: 'success',
      rejected: 'primary',
    };
    return statusMap[status] || 'neutral';
  }
}
